import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CallEnd,
  Edit,
  GraphicEq,
  Hearing,
  Mic,
  MicOff,
  PersonAdd,
  RestartAlt,
  Save,
  SettingsBackupRestore,
  Videocam,
  VideocamOff,
  Wifi,
  WifiOff,
} from '@mui/icons-material';
import { Room, RoomEvent } from 'livekit-client';
import { io } from 'socket.io-client';
import { browserLivekitUrlCandidates } from './livekitUrl';
import { enumerateStudioDevices } from './mediaDevices';
import FaderStrip from './FaderStrip';
import InvitationDialog from './InvitationDialog';
import ParticipantMedia from './ParticipantMedia';
import liveStudioApi, { getLiveStudioError } from './liveStudioApi';
import {
  loadLiveStudioSettings,
  resetLiveStudioSettings,
  saveLiveStudioSettings,
} from './liveStudioSettings';
import {
  buildMixerChannels,
  decibelsToGain,
  FADER_STRIP_GAP_PX,
  getParticipantRole,
  canManageRoom,
  isCueModeActive,
  isTerminalLiveRoom,
  mergeMixerChannels,
  mixerWidthForChannels,
  patchMixerChannel,
  shouldAttachParticipantAudio,
  shouldMonitorLocalHostAudio,
} from './mixerUtils';

const normalizeRoomPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  const nestedRoom = payload.room && typeof payload.room === 'object' ? payload.room : null;
  const room = nestedRoom ? { ...nestedRoom } : { ...payload };
  room.participants = payload.participants || room.participants || [];
  room.channels = payload.channels || room.channels || [];
  room.livekitUrl = payload.livekitUrl || room.livekitUrl || payload.url;
  room.token = payload.token || room.token || payload.accessToken;
  return room;
};

const roomIdFor = (room) => room?.id || room?.roomId || room?.sid || null;

const connectionCredentials = (payload) => ({
  livekitUrl:
    payload?.livekitUrl ||
    payload?.url ||
    payload?.room?.livekitUrl ||
    payload?.credentials?.livekitUrl,
  token:
    payload?.token ||
    payload?.accessToken ||
    payload?.room?.token ||
    payload?.credentials?.token,
});

const participantSnapshot = (participant, forceRole) => ({
  id: participant?.sid || participant?.identity,
  sid: participant?.sid,
  identity: participant?.identity,
  name: participant?.name,
  metadata: forceRole ? { role: forceRole } : participant?.metadata,
  role: forceRole,
  audioLevel: participant?.audioLevel || 0,
  muted: participant?.isMicrophoneEnabled === false,
});

const isMissingActiveRoom = (error) =>
  [204, 404].includes(error?.response?.status) ||
  error?.response?.data?.active === false;

export default function RoomConsole({
  stationId,
  regionId,
  inputDevices = [],
  outputDevices = [],
  audioInputId = '',
  audioOutputId = '',
  onAudioInputChange,
  onAudioOutputChange,
  editingLayout = false,
  onEditingLayoutChange,
  onResetLayout,
  children,
  api = liveStudioApi,
}) {
  const initialSettings = useRef(loadLiveStudioSettings()).current;
  const [roomInfo, setRoomInfo] = useState(null);
  const [roomClient, setRoomClient] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [channels, setChannels] = useState(() =>
    buildMixerChannels({ defaultFaders: initialSettings.defaultFaders })
  );
  const [defaultFaders, setDefaultFaders] = useState(initialSettings.defaultFaders);
  const [videoDevices, setVideoDevices] = useState([]);
  const [videoInputId, setVideoInputId] = useState(initialSettings.devices.videoInputId);
  const [connectionState, setConnectionState] = useState('disconnected');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [localEditingLayout, setLocalEditingLayout] = useState(false);
  const layoutEditing = onEditingLayoutChange ? editingLayout : localEditingLayout;
  const setLayoutEditing = onEditingLayoutChange || setLocalEditingLayout;
  const roomRef = useRef(null);
  const operationEpochRef = useRef(0);
  const selectionRef = useRef('');
  const audioOutputRef = useRef(audioOutputId);
  const cameraInUseRef = useRef(false);
  const activeRoomId = roomIdFor(roomInfo);
  selectionRef.current = `${stationId ?? ''}:${regionId ?? ''}`;

  const syncParticipants = useCallback((client = roomRef.current) => {
    if (!client) {
      setParticipants([]);
      return;
    }
    setParticipants([
      client.localParticipant,
      ...Array.from(client.remoteParticipants.values()).slice(0, 6),
    ]);
  }, []);

  const disconnectRoom = useCallback(async () => {
    const current = roomRef.current;
    roomRef.current = null;
    setRoomClient(null);
    setParticipants([]);
    setConnectionState('disconnected');
    if (current) {
      try {
        await current.disconnect();
      } catch {
        // The room may already have been closed by the server.
      }
    }
  }, []);

  useEffect(() => {
    const roomId = activeRoomId;
    if (!roomId) return undefined;
    const socket = io(process.env.REACT_APP_API_URL || window.location.origin, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
    });
    const onMeters = (payload) => {
      if (String(payload?.roomId) !== String(roomId) || !payload?.channels) return;
      setChannels((current) => current.map((channel) => {
        const meter = payload.channels[channel.id];
        if (!meter) return channel;
        return {
          ...channel,
          meter: decibelsToGain(meter.peakDb),
        };
      }));
    };
    const onChannel = (payload) => {
      if (String(payload?.roomId) !== String(roomId) || !payload?.channel) return;
      const channelId = payload.channel.channel_key || payload.channel.id;
      setChannels((current) => patchMixerChannel(current, channelId, payload.channel));
    };
    const onFailed = (payload) => {
      if (String(payload?.roomId) !== String(roomId)) return;
      operationEpochRef.current += 1;
      setRoomInfo(null);
      setError(payload?.message || 'Live Studio stopped unexpectedly.');
      disconnectRoom();
    };
    socket.on('live-studio:meters', onMeters);
    socket.on('live-studio:channel', onChannel);
    socket.on('live-studio:failed', onFailed);
    return () => socket.disconnect();
  }, [activeRoomId, disconnectRoom]);

  const connectRoom = useCallback(async ({ livekitUrl, token }, expectedEpoch = operationEpochRef.current) => {
    if (!livekitUrl || !token) {
      throw new Error('The room did not provide LiveKit connection credentials.');
    }
    const isCurrent = () => expectedEpoch === operationEpochRef.current;
    if (!isCurrent()) return false;
    await disconnectRoom();
    if (!isCurrent()) return false;
    setConnectionState('connecting');
    let settled = false;

    const bindRoom = (room) => {
      const sync = () => {
        if (roomRef.current === room && isCurrent()) syncParticipants(room);
      };
      [
        RoomEvent.ParticipantConnected,
        RoomEvent.ParticipantDisconnected,
        RoomEvent.TrackSubscribed,
        RoomEvent.TrackUnsubscribed,
        RoomEvent.TrackMuted,
        RoomEvent.TrackUnmuted,
        RoomEvent.LocalTrackPublished,
        RoomEvent.LocalTrackUnpublished,
        RoomEvent.ActiveSpeakersChanged,
      ].forEach((eventName) => room.on(eventName, sync));
      room.on(RoomEvent.ConnectionStateChanged, (state) => {
        if (!settled || roomRef.current !== room || !isCurrent()) return;
        setConnectionState(String(state));
      });
      room.on(RoomEvent.Reconnecting, () => {
        if (!settled || roomRef.current !== room || !isCurrent()) return;
        setConnectionState('reconnecting');
      });
      room.on(RoomEvent.Reconnected, () => {
        if (!settled || roomRef.current !== room || !isCurrent()) return;
        setConnectionState('connected');
      });
      room.on(RoomEvent.Disconnected, () => {
        if (!settled || roomRef.current !== room || !isCurrent()) return;
        setConnectionState('disconnected');
        setParticipants([]);
      });
      return sync;
    };

    const candidates = browserLivekitUrlCandidates(livekitUrl);
    if (!candidates.length) {
      throw new Error('The room did not provide a reachable LiveKit signaling URL.');
    }
    let nextRoom = null;
    let lastError = null;

    for (const url of candidates) {
      if (!isCurrent()) return false;
      if (nextRoom) {
        await Promise.resolve(nextRoom.disconnect()).catch(() => {});
      }
      nextRoom = new Room({ adaptiveStream: true, dynacast: true });
      roomRef.current = nextRoom;
      setRoomClient(nextRoom);
      const sync = bindRoom(nextRoom);
      try {
        await nextRoom.connect(url, token);
        lastError = null;
        settled = true;
        if (!isCurrent()) {
          if (roomRef.current === nextRoom) {
            roomRef.current = null;
            setRoomClient(null);
          }
          await Promise.resolve(nextRoom.disconnect()).catch(() => {});
          return false;
        }
        setConnectionState('connected');
        sync();
        if (audioOutputRef.current && typeof nextRoom.switchActiveDevice === 'function') {
          await nextRoom.switchActiveDevice('audiooutput', audioOutputRef.current).catch(() => {});
        }
        return true;
      } catch (connectError) {
        lastError = connectError;
      }
    }

    if (roomRef.current === nextRoom) {
      roomRef.current = null;
      setRoomClient(null);
      setConnectionState('disconnected');
    }
    if (nextRoom) await Promise.resolve(nextRoom.disconnect()).catch(() => {});
    if (!isCurrent()) return false;
    const tried = candidates.join(', ');
    const detail = lastError?.message || 'Encountered websocket error during connection establishment';
    throw new Error(`${detail} Tried ${tried}`);
  }, [disconnectRoom, syncParticipants]);

  const connectFromPayload = useCallback(async (
    payload,
    expectedEpoch = operationEpochRef.current
  ) => {
    const isCurrent = () => expectedEpoch === operationEpochRef.current;
    if (!isCurrent()) return false;
    const normalized = normalizeRoomPayload(payload);
    const id = roomIdFor(normalized);
    let credentials = connectionCredentials(payload);
    if ((!credentials.livekitUrl || !credentials.token) && id) {
      const tokenPayload = await api.createRoomToken(id);
      if (!isCurrent()) return false;
      credentials = connectionCredentials(tokenPayload);
    }
    const connected = await connectRoom(credentials, expectedEpoch);
    if (!connected || !isCurrent()) return false;
    if (id) {
      const refreshed = normalizeRoomPayload(await api.getRoom(id));
      if (!isCurrent()) return false;
      if (refreshed) setRoomInfo(refreshed);
    }
    return true;
  }, [api, connectRoom]);

  useEffect(() => {
    let cancelled = false;
    const epoch = ++operationEpochRef.current;
    const isCurrent = () => !cancelled && epoch === operationEpochRef.current;
    const loadActiveRoom = async () => {
      await disconnectRoom();
      if (!isCurrent()) return;
      setRoomInfo(null);
      setBusy(false);
      setLoading(false);
      setError('');
      setNotice('');
      if (stationId === '' || stationId === null || stationId === undefined) return;

      setLoading(true);
      try {
        const payload = await api.getActiveRoom(stationId, regionId);
        const room = normalizeRoomPayload(payload);
        if (isCurrent() && roomIdFor(room) && room?.active !== false && room?.status !== 'ended') {
          setRoomInfo(room);
          if (canManageRoom(room)) {
            await connectFromPayload(payload, epoch);
          } else {
            setConnectionState('read-only');
          }
        }
      } catch (requestError) {
        if (isCurrent() && !isMissingActiveRoom(requestError)) {
          setError(getLiveStudioError(requestError, 'Could not load the active studio room.'));
        }
      } finally {
        if (isCurrent()) setLoading(false);
      }
    };
    loadActiveRoom();
    return () => {
      cancelled = true;
      if (operationEpochRef.current === epoch) operationEpochRef.current += 1;
    };
  }, [api, connectFromPayload, disconnectRoom, regionId, stationId]);

  useEffect(() => () => {
    operationEpochRef.current += 1;
    const current = roomRef.current;
    roomRef.current = null;
    if (current) Promise.resolve(current.disconnect()).catch(() => {});
  }, []);

  useEffect(() => {
    if (!roomInfo) {
      setChannels(buildMixerChannels({ defaultFaders }));
      return;
    }
    const liveParticipants = participants.map((participant, index) =>
      participantSnapshot(participant, index === 0 ? 'host' : undefined)
    );
    const incoming = buildMixerChannels({
      channels: roomInfo.channels,
      participants: [...(roomInfo.participants || []), ...liveParticipants],
      defaultFaders,
    });
    setChannels((current) => mergeMixerChannels(incoming, current));
  }, [defaultFaders, participants, roomInfo]);

  useEffect(() => {
    const id = roomIdFor(roomInfo);
    if (!id) return undefined;
    const selection = selectionRef.current;
    let cancelled = false;
    const interval = setInterval(async () => {
      try {
        const payload = await api.getRoom(id);
        if (cancelled || selectionRef.current !== selection) return;
        const room = normalizeRoomPayload(payload);
        if (isTerminalLiveRoom(room)) {
          operationEpochRef.current += 1;
          setRoomInfo(null);
          await disconnectRoom();
          if (room?.status === 'failed') {
            setError(room?.state?.bridge_error || 'Live Studio stopped unexpectedly.');
          }
        } else if (room) {
          setRoomInfo(room);
        }
      } catch {
        // Connection state remains the authoritative operator-facing signal.
      }
    }, 8000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [activeRoomId, api, disconnectRoom]);

  useEffect(() => {
    const applyCameras = (cameras) => {
      setVideoDevices(cameras);
      setVideoInputId((current) => {
        if (current && cameras.some((device) => device.deviceId === current)) return current;
        return cameras[0]?.deviceId || '';
      });
    };
    const enumerateVideoDevices = async ({ allowProbe = false } = {}) => {
      const next = await enumerateStudioDevices({
        probeVideo: true,
        allowProbe: allowProbe && !cameraInUseRef.current,
      });
      applyCameras(next.videoInputs);
    };
    enumerateVideoDevices({ allowProbe: true }).catch(() => {});
    const onDeviceChange = () => enumerateVideoDevices({ allowProbe: true }).catch(() => {});
    navigator.mediaDevices?.addEventListener?.('devicechange', onDeviceChange);
    return () => navigator.mediaDevices?.removeEventListener?.('devicechange', onDeviceChange);
  }, []);

  useEffect(() => {
    if (!videoInputId) return;
    saveLiveStudioSettings({ devices: { videoInputId } });
  }, [videoInputId]);

  useEffect(() => {
    audioOutputRef.current = audioOutputId;
    if (!roomClient || !audioOutputId || typeof roomClient.switchActiveDevice !== 'function') return;
    roomClient.switchActiveDevice('audiooutput', audioOutputId).catch(() => {
      setError('The selected monitor output could not be applied.');
    });
  }, [audioOutputId, roomClient]);

  const startRoom = async () => {
    if (stationId === '' || stationId === null || stationId === undefined) {
      setError('Select a station before starting Live Studio.');
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    const epoch = ++operationEpochRef.current;
    try {
      const payload = await api.createRoom({ stationId, regionId });
      if (epoch !== operationEpochRef.current) return;
      const room = normalizeRoomPayload(payload);
      if (!roomIdFor(room)) throw new Error('The server created a room without an identifier.');
      setRoomInfo(room);
      const connected = await connectFromPayload(payload, epoch);
      if (connected && epoch === operationEpochRef.current) {
        setNotice('Live Studio room started.');
      }
    } catch (requestError) {
      if (epoch === operationEpochRef.current) {
        setError(getLiveStudioError(requestError, 'Could not start Live Studio.'));
      }
    } finally {
      if (epoch === operationEpochRef.current) setBusy(false);
    }
  };

  const reconnect = async () => {
    const id = roomIdFor(roomInfo);
    if (!id) return;
    setBusy(true);
    setError('');
    const epoch = ++operationEpochRef.current;
    try {
      const payload = await api.createRoomToken(id);
      if (epoch !== operationEpochRef.current) return;
      await connectRoom(connectionCredentials(payload), epoch);
    } catch (requestError) {
      if (epoch === operationEpochRef.current) {
        setError(getLiveStudioError(requestError, 'Could not reconnect to Live Studio.'));
      }
    } finally {
      if (epoch === operationEpochRef.current) setBusy(false);
    }
  };

  const endRoom = async () => {
    const id = roomIdFor(roomInfo);
    if (!id || !window.confirm('End this Live Studio room for everyone?')) return;
    setBusy(true);
    setError('');
    try {
      await api.endRoom(id);
      await disconnectRoom();
      setRoomInfo(null);
      setNotice('Live Studio room ended.');
    } catch (requestError) {
      setError(getLiveStudioError(requestError, 'Could not end the room.'));
    } finally {
      setBusy(false);
    }
  };

  const updateChannel = async (channelId, changes) => {
    if (!canManageRoom(roomInfo)) return;
    let snapshot = null;
    setChannels((current) => {
      snapshot = current;
      return patchMixerChannel(current, channelId, changes);
    });
    const id = roomIdFor(roomInfo);
    if (!id) return;
    try {
      const result = await api.updateChannel(id, channelId, changes);
      const serverChannel = result?.channel || result;
      if (serverChannel) {
        setChannels((current) => patchMixerChannel(current, channelId, serverChannel));
      }
    } catch (requestError) {
      if (snapshot) setChannels(snapshot);
      setError(getLiveStudioError(requestError, `Could not update ${channelId}.`));
    }
  };

  const toggleMicrophone = async () => {
    const local = roomClient?.localParticipant;
    if (!local) return;
    setError('');
    try {
      if (!local.isMicrophoneEnabled && audioInputId && roomClient.switchActiveDevice) {
        await roomClient.switchActiveDevice('audioinput', audioInputId);
      }
      await local.setMicrophoneEnabled(!local.isMicrophoneEnabled);
      syncParticipants(roomClient);
    } catch (mediaError) {
      setError(getLiveStudioError(mediaError, 'Microphone access failed.'));
    }
  };

  const toggleCamera = async () => {
    const local = roomClient?.localParticipant;
    if (!local) return;
    setError('');
    const enable = !local.isCameraEnabled;
    if (enable) cameraInUseRef.current = true;
    try {
      if (enable && videoInputId && roomClient.switchActiveDevice) {
        await roomClient.switchActiveDevice('videoinput', videoInputId);
      }
      await local.setCameraEnabled(enable);
      cameraInUseRef.current = Boolean(local.isCameraEnabled);
      syncParticipants(roomClient);
    } catch (mediaError) {
      cameraInUseRef.current = Boolean(local.isCameraEnabled);
      setError(getLiveStudioError(mediaError, 'Camera access failed.'));
    }
  };

  const enablePlayback = async () => {
    try {
      await roomClient?.startAudio?.();
      setNotice('Studio return audio enabled.');
    } catch {
      setError('Browser audio playback is still blocked.');
    }
  };

  const saveFaderDefaults = () => {
    const nextDefaults = {
      program: channels.find((channel) => channel.role === 'program')?.gain ?? defaultFaders.program,
      host: channels.find((channel) => channel.role === 'host')?.gain ?? defaultFaders.host,
      guest: channels.find((channel) => channel.role === 'guest')?.gain ?? defaultFaders.guest,
    };
    setDefaultFaders(nextDefaults);
    saveLiveStudioSettings({ defaultFaders: nextDefaults });
    setNotice('Default fader levels saved.');
  };

  const resetConsole = () => {
    const defaults = resetLiveStudioSettings();
    saveLiveStudioSettings({
      stationId,
      devices: { audioInputId, audioOutputId, videoInputId: '' },
    });
    setDefaultFaders(defaults.defaultFaders);
    setVideoInputId('');
    onResetLayout?.(defaults);
    setNotice('Local studio layout and defaults reset.');
  };

  const active = Boolean(roomIdFor(roomInfo));
  const canManage = canManageRoom(roomInfo);
  const connected = connectionState === 'connected';
  const cueActive = isCueModeActive(channels);
  const monitorLocalAudio = shouldMonitorLocalHostAudio(channels);
  const mixerWidth = mixerWidthForChannels(channels.length);
  const attachAudio = useCallback(
    (participant) => shouldAttachParticipantAudio(participant, channels),
    [channels]
  );
  const serverGuestCount = (roomInfo?.participants || [])
    .filter((participant) =>
      getParticipantRole(participant) === 'guest' && participant.status !== 'removed'
    )
    .length;
  const localParticipant = roomClient?.localParticipant;
  cameraInUseRef.current = Boolean(localParticipant?.isCameraEnabled);
  const liveGuestCount = participants
    .filter((participant) => participant !== localParticipant && getParticipantRole(participant) === 'guest')
    .length;
  const inviteSeatCount = (roomInfo?.invites || []).filter((invite) =>
    ['pending', 'joined'].includes(invite.status) &&
    (!invite.expires_at || new Date(invite.expires_at) > new Date())
  ).length;
  const guestCount = Math.min(5, Math.max(serverGuestCount, liveGuestCount, inviteSeatCount));

  const participantsPanel = (
    <Card sx={{ height: '100%', overflow: 'auto', bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
      <CardContent sx={{ p: 1.25, height: '100%', '&:last-child': { pb: 1.25 } }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Participant video &amp; audio</Typography>
        <ParticipantMedia
          participants={participants}
          localParticipant={localParticipant}
          shouldAttachAudio={attachAudio}
          monitorLocalAudio={monitorLocalAudio}
          audioOutputId={audioOutputId}
        />
      </CardContent>
    </Card>
  );

  const mixerPanel = (
    <Card sx={{ height: '100%', overflow: 'hidden', bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
      <CardContent
        className="live-studio-no-drag"
        sx={{
          height: '100%',
          p: 1.25,
          '&:last-child': { pb: 1.25 },
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75, gap: 1 }}>
          <Box>
            <Typography variant="subtitle2">Server mix</Typography>
            <Typography variant="caption" color="text.secondary">
              Cue = headphones only · Mute / Off air keep strips off broadcast
            </Typography>
          </Box>
          <Button size="small" startIcon={<Save />} onClick={saveFaderDefaults}>
            Save defaults
          </Button>
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: `${FADER_STRIP_GAP_PX}px`,
            overflowX: 'auto',
            flex: 1,
            alignItems: 'stretch',
            pb: 0.5,
            minWidth: 0,
            width: '100%',
          }}
        >
          {channels.map((channel) => (
            <FaderStrip
              key={channel.id}
              channel={channel}
              disabled={!active || !canManage}
              onGainPreview={(channelId, gain) =>
                setChannels((current) => patchMixerChannel(current, channelId, { gain }))
              }
              onGainCommit={(channelId, gain) => updateChannel(channelId, { gain })}
              onToggleMute={(item) => updateChannel(item.id, { muted: !item.muted })}
              onTogglePfl={(item) => updateChannel(item.id, { pfl: !item.pfl })}
              onToggleOnAir={(item) => updateChannel(item.id, { onAir: !item.onAir })}
            />
          ))}
        </Box>
      </CardContent>
    </Card>
  );

  const workspace = {
    participantsPanel,
    mixerPanel,
    cueActive,
    channels,
    layoutEditing,
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderColor: active ? 'var(--rs-live-console-border-active)' : 'var(--rs-live-console-border)',
        background:
          'radial-gradient(700px 260px at 0% 0%, var(--rs-live-console-glow), transparent 65%), var(--rs-live-console-bg)',
      }}
    >
      <Box
        sx={{
          px: { xs: 1.5, md: 2 },
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.5,
          bgcolor: 'var(--rs-live-console-bar)',
          borderBottom: '1px solid var(--rs-live-console-border)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              bgcolor: active ? 'rgba(239,68,68,0.16)' : 'rgba(34,211,238,0.12)',
              color: active ? '#f87171' : 'var(--rs-accent)',
            }}
          >
            <GraphicEq />
          </Box>
          <Box>
            <Typography variant="h6">Remote Studio</Typography>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Chip
                size="small"
                icon={connected ? <Wifi /> : <WifiOff />}
                label={active ? (canManage ? connectionState : 'Read only') : 'No active room'}
                color={connected ? 'success' : active ? 'warning' : 'default'}
                sx={{ height: 21, textTransform: 'capitalize' }}
              />
              {active && <Typography variant="caption" color="text.secondary">{guestCount}/5 guests</Typography>}
            </Stack>
          </Box>
        </Box>

        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {!active ? (
            <Button variant="contained" color="error" disabled={busy || loading} onClick={startRoom}>
              Start room
            </Button>
          ) : (
            <>
              {canManage && (
                <>
                  <Tooltip title="Ask the browser to start return audio">
                    <Button size="small" variant="outlined" startIcon={<Hearing />} onClick={enablePlayback}>
                      Monitor
                    </Button>
                  </Tooltip>
                  <Button
                    size="small"
                    variant={localParticipant?.isMicrophoneEnabled ? 'contained' : 'outlined'}
                    color={localParticipant?.isMicrophoneEnabled ? 'success' : 'inherit'}
                    startIcon={localParticipant?.isMicrophoneEnabled ? <Mic /> : <MicOff />}
                    onClick={toggleMicrophone}
                    disabled={!connected}
                  >
                    Mic
                  </Button>
                  <Tooltip title={connected ? 'Publish the studio camera into the room' : 'Connect Remote Studio before enabling the camera'}>
                    <span>
                      <Button
                        size="small"
                        variant={localParticipant?.isCameraEnabled ? 'contained' : 'outlined'}
                        color={localParticipant?.isCameraEnabled ? 'info' : 'inherit'}
                        startIcon={localParticipant?.isCameraEnabled ? <Videocam /> : <VideocamOff />}
                        onClick={toggleCamera}
                        disabled={!connected}
                      >
                        Camera
                      </Button>
                    </span>
                  </Tooltip>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<PersonAdd />}
                    onClick={() => setInviteOpen(true)}
                    disabled={guestCount >= 5}
                  >
                    Invite
                  </Button>
                  {!connected && (
                    <Button size="small" variant="outlined" startIcon={<RestartAlt />} onClick={reconnect} disabled={busy}>
                      Reconnect
                    </Button>
                  )}
                  <Button size="small" color="error" variant="outlined" startIcon={<CallEnd />} onClick={endRoom} disabled={busy}>
                    End
                  </Button>
                </>
              )}
            </>
          )}
          <Button
            size="small"
            variant={layoutEditing ? 'contained' : 'outlined'}
            startIcon={<Edit />}
            onClick={() => setLayoutEditing(!layoutEditing)}
          >
            {layoutEditing ? 'Done' : 'Layout'}
          </Button>
          <Tooltip title="Reset saved layout, devices and fader defaults">
            <Button size="small" variant="outlined" startIcon={<SettingsBackupRestore />} onClick={resetConsole}>
              Reset
            </Button>
          </Tooltip>
        </Stack>
      </Box>

      <CardContent
        sx={{
          p: { xs: 1.25, md: 2 },
          '&:last-child': { pb: 2 },
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: typeof children === 'function' ? 'auto' : 'hidden',
        }}
      >
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">Checking for an active room…</Typography>
          </Box>
        )}
        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 1.5 }}>{error}</Alert>}
        {notice && <Alert severity="success" onClose={() => setNotice('')} sx={{ mb: 1.5 }}>{notice}</Alert>}
        {cueActive && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Cue mode — monitor solos cued strips only. Mute / Off air still control the broadcast mix.
          </Alert>
        )}
        {layoutEditing && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Drag and resize widgets to arrange Live Studio. Layout is saved locally on this browser.
          </Alert>
        )}
        {active && !canManage && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            {roomInfo?.owner_username
              ? `${roomInfo.owner_username} is hosting this room. Mixer and host controls are read only.`
              : 'Another operator is hosting this room. Mixer and host controls are read only.'}
          </Alert>
        )}

        <Box
          className="live-studio-no-drag"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
            gap: 1,
            mb: 1.5,
            flexShrink: 0,
          }}
        >
          <FormControl size="small">
            <InputLabel>Studio microphone</InputLabel>
            <Select
              value={audioInputId}
              label="Studio microphone"
              onChange={(event) => onAudioInputChange?.(event.target.value)}
            >
              {inputDevices.map((device) => (
                <MenuItem key={device.deviceId} value={device.deviceId}>
                  {device.label || `Microphone ${device.deviceId.slice(0, 8)}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>Monitor output</InputLabel>
            <Select
              value={audioOutputId}
              label="Monitor output"
              onChange={(event) => onAudioOutputChange?.(event.target.value)}
            >
              {outputDevices.map((device) => (
                <MenuItem key={device.deviceId} value={device.deviceId}>
                  {device.label || `Output ${device.deviceId.slice(0, 8)}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>Studio camera</InputLabel>
            <Select
              value={videoInputId}
              label="Studio camera"
              onChange={(event) => setVideoInputId(event.target.value)}
              displayEmpty
            >
              {videoDevices.length === 0 && (
                <MenuItem value="">
                  {typeof window !== 'undefined' && window.isSecureContext === false
                    ? 'Camera needs HTTPS'
                    : 'No camera found — allow camera access'}
                </MenuItem>
              )}
              {videoDevices.map((device) => (
                <MenuItem key={device.deviceId} value={device.deviceId}>
                  {device.label || `Camera ${device.deviceId.slice(0, 8)}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {typeof children === 'function' ? (
          <Box sx={{ flex: 1, minHeight: 480 }}>{children(workspace)}</Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 1.5,
            }}
          >
            <Box sx={{ flex: '1 1 auto', minWidth: 0, minHeight: { xs: 220, md: 0 } }}>
              {participantsPanel}
            </Box>
            <Box
              sx={{
                flex: '0 0 auto',
                width: { xs: '100%', md: mixerWidth },
                maxWidth: { xs: '100%', md: '55%' },
                minWidth: { xs: 0, md: Math.min(mixerWidth, 280) },
              }}
            >
              {mixerPanel}
            </Box>
          </Box>
        )}
      </CardContent>

      {canManage && (
        <InvitationDialog
          open={inviteOpen}
          roomId={roomIdFor(roomInfo)}
          invites={roomInfo?.invites || []}
          onChanged={async () => {
            const id = roomIdFor(roomInfo);
            if (!id) return;
            try {
              setRoomInfo(normalizeRoomPayload(await api.getRoom(id)));
            } catch (requestError) {
              setError(getLiveStudioError(requestError, 'Could not refresh invitations.'));
            }
          }}
          onClose={() => setInviteOpen(false)}
          api={api}
        />
      )}
    </Card>
  );
}
